import { __ } from '@wordpress/i18n';
import { format, getSettings } from '@wordpress/date';
import { parseISO } from 'date-fns';

export const formatTimeRange = (
	startTime: string,
	blockSize: number,
	showEndTime: boolean = true
): string => {
	const start = parseISO(`2000-01-01T${startTime}`);
	const timeFormat = getSettings().formats.time;
	const startFormatted = format(timeFormat, start);

	if (!showEndTime) {
		return startFormatted;
	}

	const end = new Date(start.getTime() + blockSize * 60 * 1000);
	const endFormatted = format(timeFormat, end);

	return `${startFormatted} - ${endFormatted}`;
};

export const formatTime = (time: Date): string => {
	const timeFormat = getSettings().formats.time;
	return format(timeFormat, time);
};

export const formatDuration = (minutes: number): string => {
	const days = Math.floor(minutes / (24 * 60));
	const remainingHours = Math.floor((minutes % (24 * 60)) / 60);
	const remainingMinutes = minutes % 60;

	if (days > 0) {
		if (remainingHours === 0) {
			return days === 1
				? __('1 day', 'gp-bookings')
				: /* translators: %d: number of days */
					__('%d days', 'gp-bookings').replace('%d', days.toString());
		}

		if (days === 1) {
			return remainingHours === 1
				? __('1 day 1 hour', 'gp-bookings')
				: /* translators: %d: number of hours */
					__('1 day %d hours', 'gp-bookings').replace(
						'%d',
						remainingHours.toString()
					);
		}

		return remainingHours === 1
			? /* translators: %d: number of days */
				__('%d days 1 hour', 'gp-bookings').replace(
					'%d',
					days.toString()
				)
			: /* translators: 1: number of days, 2: number of hours */
				__('%d days %d hours', 'gp-bookings')
					.replace('%d', days.toString())
					.replace('%d', remainingHours.toString());
	}

	if (minutes < 60) {
		return minutes === 1
			? __('1 minute', 'gp-bookings')
			: /* translators: %d: number of minutes */
				__('%d minutes', 'gp-bookings').replace(
					'%d',
					minutes.toString()
				);
	}

	const hours = Math.floor(minutes / 60);

	if (remainingMinutes === 0) {
		return hours === 1
			? __('1 hour', 'gp-bookings')
			: /* translators: %d: number of hours */
				__('%d hours', 'gp-bookings').replace('%d', hours.toString());
	}

	if (hours === 1) {
		return remainingMinutes === 1
			? __('1 hour 1 minute', 'gp-bookings')
			: /* translators: %d: number of minutes */
				__('1 hour %d minutes', 'gp-bookings').replace(
					'%d',
					remainingMinutes.toString()
				);
	}

	return remainingMinutes === 1
		? /* translators: %d: number of hours */
			__('%d hours 1 minute', 'gp-bookings').replace(
				'%d',
				hours.toString()
			)
		: /* translators: 1: number of hours, 2: number of minutes */
			__('%d hours %d minutes', 'gp-bookings')
				.replace('%d', hours.toString())
				.replace('%d', remainingMinutes.toString());
};

export const formatFullDate = (date: Date): string => {
	const dateFormat = getSettings().formats.date;
	return format(dateFormat, date);
};

/**
 * Format a Date object to YYYY-MM-DD string without timezone conversion
 *
 * This function is crucial for preserving user-selected calendar dates.
 * Using date.toISOString() would convert the date to UTC, potentially shifting
 * the calendar date to a different day when timezone offsets are applied.
 *
 * @param date - The Date object to format
 * @return A string in YYYY-MM-DD format representing the calendar date
 *
 * @example
 * // User in Calcutta (UTC+5:30) selects January 15, 2025
 * const date = new Date(2025, 0, 15); // January 15, 2025 at midnight local time
 *
 * // WRONG: date.toISOString().split('T')[0]
 * // Would return "2025-01-14" because it converts to UTC first
 *
 * // CORRECT: formatCalendarDate(date)
 * // Returns "2025-01-15" preserving the calendar date
 */
export const formatCalendarDate = (date: Date): string => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

/**
 * Parse a YYYY-MM-DD string as a date in the browser's local timezone.
 *
 * Passing a date-only string directly to the Date constructor interprets it as
 * UTC, which can shift the calendar date back one day in western timezones.
 */
export const parseCalendarDate = (date: string): Date => {
	const [year, month, day] = date.split('-').map(Number);
	return new Date(year, month - 1, day);
};
